import type {ReactElement} from 'react';
import React from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {useUpdateEffect} from 'ahooks';
import {ContextualMenu} from '@Components/contextual-menu';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import type {BottomBarItemProps} from '@Components/contextual-menu/components/bottom-bar-item';
import styles from './poster-editor-item-stretch-panel.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';

interface PosterEditorItemStretchPanelProps {
  panelId: string;
}

enum StretchOptions {
  VERTICAL = 'VERTICAL',
  HORIZONTAL = 'HORIZONTAL',
}

export default function PosterEditorItemStretchPanel({...props}: PosterEditorItemStretchPanelProps): ReactElement {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;

  useUpdateEffect((): void => {
    onClose();
  }, [activeItem]);

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_stretch')} />;
  };

  const onStretchVertical = (): void => {
    void activeItem?.stretchToCanvasVertically();
  };
  const onStretchHorizontal = (): void => {
    void activeItem?.stretchToCanvasHorizontally();
  };

  const getItems = (): BottomBarItemProps[] => {
    return [
      {
        id: StretchOptions.VERTICAL,
        icon: 'icon-stretch-vertical',
        text: window.i18next.t('pmwjs_vertical'),
        className: styles.item,
      },
      {
        id: StretchOptions.HORIZONTAL,
        icon: 'icon-stretch-horizontal',
        text: window.i18next.t('pmwjs_horizontal'),
        className: styles.item,
      },
    ];
  };

  const onClickHandler = (id: StretchOptions): void => {
    switch (id) {
      case StretchOptions.HORIZONTAL:
        onStretchHorizontal();
        break;
      case StretchOptions.VERTICAL:
        onStretchVertical();
        break;
      default:
        break;
    }
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu
        id="stretch-mobile-panel"
        includeLeftRightPadding={false}
        contentClassName={`spacing-m-0 spacing-p-l-1 spacing-p-r-1 spacing-p-t-1 spacing-p-b-4 ${styles.container}`}
      >
        <BottomBar isForEditorBottomBar={false} id="stretch-bottom-bar" items={getItems()} className={`spacing-p-l-0 ${styles.bottomBar}`} onClick={onClickHandler} />
      </ContextualMenu>
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}
